import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useSearchParams } from "react-router-dom"; 

import { get } from "@/lib/api-client";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { ProductCard } from "./components/ProductCard";
import type { Category, Product } from "@/types/catalog";

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  
  // 🛠️ DEEP FIX: Get exact category ID directly from the URL safely
  const activeCategoryId = searchParams.get("category") || "";

  const cats = useQuery({
    queryKey: ["categories-all"],
    queryFn: () => get<Category[]>("/catalog/categories"),
  });
  
  // 🛠️ FIXED: Send the activeCategoryId directly to the backend API!
  // The backend will now do the heavy lifting and return ONLY the matching products.
  const products = useQuery({
    queryKey: ["products-all", activeCategoryId],
    queryFn: async () => {
      // Create explicit query parameters object
      const params: Record<string, string> = {};
      if (activeCategoryId) {
        params.category = activeCategoryId; // This sends ?category=2 to backend
      }
      return get<Product[]>("/catalog/products", params);
    }
  });

  // Client-side filtering is now ONLY used for the text search bar
  const filtered = useMemo(() => {
    let list = products.data ?? [];
    
    // Force client-side filter fallback just in case the backend totally fails to filter
    if (activeCategoryId) {
      list = list.filter((p) => String(p.category?.id) === String(activeCategoryId));
    }
    
    // Filter by Search Term
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.product_code.toLowerCase().includes(q)
      );
    }
    
    return list;
  }, [products.data, search, activeCategoryId]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val) {
      setSearchParams({ category: val });
    } else {
      setSearchParams({}); 
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Catalog</h1>
        <p className="text-sm text-surface-500 mt-1">
          Browse products. Each purchase awards points toward Club Entry creation.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-[1fr_240px]">
        <Input
          placeholder="Search products by name or code…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />
        <Select
          value={activeCategoryId} 
          onChange={handleCategoryChange}
          placeholder="All categories"
          options={(cats.data ?? []).map((c) => ({ value: String(c.id), label: c.name }))}
        />
      </div>

      {products.isLoading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-72 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="No products found" description="Try adjusting your filters or search terms." />
      ) : (
        <div className="grid gap-4 sm:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}